import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  Skeleton,
  Alert,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import config from "../../config";
import Sidebar from "../Sidebar/Sidebar";
import EditManualReportDialog from "./EditManualReportDialoge"; 
import AddManualReportDialog from "./Addmanualreportdialog"; 
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const drawerWidth = 270;

const DATE_FILTER_STORAGE_KEY = "ivrManualDateFilter";
const IS_LIFETIME_STORAGE_KEY = "ivrManualIsLifetime";
const STATE_FILTER_STORAGE_KEY = "ivrManualStateFilter";
const MANDI_FILTER_STORAGE_KEY = "ivrManualMandiFilter";
const VARIETY_FILTER_STORAGE_KEY = "ivrManualVarietyFilter";
const PAGE_STORAGE_KEY = "ivrManualPage";
const ROWS_PER_PAGE_STORAGE_KEY = "ivrManualRowsPerPage";

const MOBILE_COLUMN_COUNT = 6;
const DESKTOP_COLUMN_COUNT = 11;
const emptyFilterOptions = { states: [], mandis: [], varieties: [] };
const DEFAULT_IS_LIFETIME = false;

const toLocalDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDate = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? "-" : number.toLocaleString("en-IN");
};

const formatBuyers = (buyers) => {
  if (!Array.isArray(buyers) || buyers.length === 0) return "";
  return buyers.map((b) => b?.buyerName).filter(Boolean).join(", ");
};

const asLabel = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.mandiLocation || v.location || v.name || v.label || "";
};
const asId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v._id || "";
};

const parsePriceRange = (text) => {
  if (text === undefined || text === null) return { min: null, max: null };
  const str = String(text).trim();
  if (!str || str === "-") return { min: null, max: null };
  const nums = (str.match(/\d+(?:\.\d+)?/g) || []).map(Number).filter((n) => !Number.isNaN(n));
  if (nums.length === 0) return { min: null, max: null };
  if (nums.length === 1) return { min: nums[0], max: nums[0] };
  return { min: nums[0], max: nums[1] };
};

const formatPriceRange = (min, max) => {
  if (min === null || max === null) return null;
  if (min === max) return `₹${formatNumber(min)}`;
  return `₹${formatNumber(min)} - ₹${formatNumber(max)}`;
};

const normalizeRow = (item, idx) => {
  const { min: handPriceMin, max: handPriceMax } = parsePriceRange(item.handPrice);
  const { min: combineMin, max: combineMax } = parsePriceRange(item.combinePrice);

  let combinePrice = null;
  if (combineMin !== null) {
    combinePrice = combineMin === combineMax ? `${combineMin}` : `${combineMin}-${combineMax}`;
  }

  const totalArrival =
    item.totalArrival !== undefined && item.totalArrival !== null && item.totalArrival !== ""
      ? Number(item.totalArrival) || 0
      : Number(item.response?.marketTotal) || 0;

  return {
    key: `${item._id || item.mandi || "mandi"}-${item.variety || "variety"}-${idx}`,

    id: item._id,
    mandiPartnerId:
      asId(item.mandiPartnerId) ||
      asId(item.mandiPartner) ||
      asId(item.mandiLocation) ||
      asId(item.location) ||
      asId(item.mandiPartnerName) ||
      "",
    varietyId: item.varietyId || "",
    marketTotal: Number(item.response?.marketTotal) || totalArrival,
    combinePriceMin: combineMin ?? 0,
    combinePriceMax: combineMax ?? 0,

    state: item.state || "-",
    mandi: asLabel(item.mandiLocation) || asLabel(item.location) || asLabel(item.mandi) || "-",
    variety: item.variety || "-",

    totalArrival,

    handQty:
      item.handQtyNumber !== undefined && item.handQtyNumber !== null
        ? Number(item.handQtyNumber) || 0
        : Number(String(item.handQty || "0").replace(/,/g, "")) || 0,

    combineQty:
      item.combineQtyNumber !== undefined && item.combineQtyNumber !== null
        ? Number(item.combineQtyNumber) || 0
        : Number(String(item.combineQty || "0").replace(/,/g, "")) || 0,

    handPriceMin,
    handPriceMax,
    combinePrice,

    buyers: Array.isArray(item.buyers)
      ? item.buyers.map((b) => ({
        buyerId: b?.buyerId || b?._id || "",
        buyerName: b?.buyerName || "-",
        purchaseQuantity: b?.purchaseQuantity ?? null,
      }))
      : [],

    callDate: item.callDate || null,
    remarks: item.remarks || "",
  };
};

const getPageNumbers = (current, total) => {
  const delta = 1;
  const range = [];
  const withDots = [];
  let last;

  for (let i = 0; i < total; i++) {
    if (i === 0 || i === total - 1 || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (last !== undefined) {
      if (i - last === 2) {
        withDots.push(last + 1);
      } else if (i - last > 1) {
        withDots.push("...");
      }
    }
    withDots.push(i);
    last = i;
  });

  return withDots;
};

const IvrCallLogs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleViewChange = (_, value) => {
    if (!value || value === "table") return;
    navigate("/ivr-anaylitics");
  };

  const [reportStats, setReportStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState(emptyFilterOptions);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const todayValue = useMemo(() => toLocalDateInputValue(new Date()), []);

  const [editRow, setEditRow] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  const getStoredValue = (key, fallback) => {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? stored : fallback;
  };

  const [isLifetime, setIsLifetime] = useState(() => getStoredValue(IS_LIFETIME_STORAGE_KEY, "false") === "true");
  const [dateFilter, setDateFilter] = useState(() => getStoredValue(DATE_FILTER_STORAGE_KEY, todayValue));
  const [pendingDate, setPendingDate] = useState(() => getStoredValue(DATE_FILTER_STORAGE_KEY, todayValue));
  const [pendingLifetime, setPendingLifetime] = useState(() => getStoredValue(IS_LIFETIME_STORAGE_KEY, "false") === "true");
  const [stateFilter, setStateFilter] = useState(() => getStoredValue(STATE_FILTER_STORAGE_KEY, ""));
  const [mandiFilter, setMandiFilter] = useState(() => getStoredValue(MANDI_FILTER_STORAGE_KEY, ""));
  const [varietyFilter, setVarietyFilter] = useState(() => getStoredValue(VARIETY_FILTER_STORAGE_KEY, ""));
  const [pendingState, setPendingState] = useState(() => getStoredValue(STATE_FILTER_STORAGE_KEY, ""));
  const [pendingMandi, setPendingMandi] = useState(() => getStoredValue(MANDI_FILTER_STORAGE_KEY, ""));
  const [pendingVariety, setPendingVariety] = useState(() => getStoredValue(VARIETY_FILTER_STORAGE_KEY, ""));

  const [page, setPage] = useState(() => {
    const parsed = parseInt(getStoredValue(PAGE_STORAGE_KEY, "0"), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const parsed = parseInt(getStoredValue(ROWS_PER_PAGE_STORAGE_KEY, "10"), 10);
    return Number.isNaN(parsed) ? 10 : parsed;
  });

  const token = sessionStorage.getItem("token");
  const hasPendingChange =
    pendingLifetime !== isLifetime ||
    (!pendingLifetime && pendingDate !== dateFilter) ||
    pendingState !== stateFilter ||
    pendingMandi !== mandiFilter ||
    pendingVariety !== varietyFilter;

  const hasAppliedFilter =
    isLifetime !== DEFAULT_IS_LIFETIME ||
    (!isLifetime && dateFilter !== todayValue) ||
    Boolean(stateFilter) ||
    Boolean(mandiFilter) ||
    Boolean(varietyFilter);

  const abortRef = useRef(null);
  const isFirstRender = useRef(true);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(
    async (signal, requestId) => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();
        if (isLifetime) {
          params.set("dateType", "lifetime");
        } else if (dateFilter) {
          params.set("date", dateFilter);
        }
        if (stateFilter) params.set("state", stateFilter);
        if (mandiFilter) params.set("mandi", mandiFilter);
        if (varietyFilter) params.set("variety", varietyFilter);
        params.set("page", String(page + 1));
        params.set("limit", String(rowsPerPage));

        const res = await fetch(`${config.apiBaseUrl}/campaign-call/get-manual-report?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          signal,
        });

        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

        const json = await res.json();
        if (json?.success === false) throw new Error(json?.message || "Failed to load IVR manual reports.");

        if (requestIdRef.current !== requestId) return;

        setReportStats(json?.reportStats ?? null);
        setFilterOptions({
          states: Array.isArray(json?.filters?.states) ? json.filters.states : [],
          mandis: Array.isArray(json?.filters?.mandiLocations) ? json.filters.mandiLocations : [],
          varieties: Array.isArray(json?.filters?.varieties) ? json.filters.varieties : [],
        });
        setRows(Array.isArray(json?.data) ? json.data.map(normalizeRow) : []);
        setPagination(json?.pagination ?? null);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (requestIdRef.current !== requestId) return;
        setError(err.message || "Failed to load IVR manual reports.");
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [token, isLifetime, dateFilter, stateFilter, mandiFilter, varietyFilter, page, rowsPerPage, refreshKey]
  );

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;
    fetchData(controller.signal, requestId);

    return () => controller.abort();
  }, [fetchData, isLifetime, dateFilter, stateFilter, mandiFilter, varietyFilter, page, rowsPerPage, refreshKey]);

  useEffect(() => {
    sessionStorage.setItem(DATE_FILTER_STORAGE_KEY, dateFilter);
  }, [dateFilter]);

  useEffect(() => {
    sessionStorage.setItem(IS_LIFETIME_STORAGE_KEY, String(isLifetime));
  }, [isLifetime]);

  useEffect(() => {
    sessionStorage.setItem(STATE_FILTER_STORAGE_KEY, stateFilter);
  }, [stateFilter]);

  useEffect(() => {
    sessionStorage.setItem(MANDI_FILTER_STORAGE_KEY, mandiFilter);
  }, [mandiFilter]);

  useEffect(() => {
    sessionStorage.setItem(VARIETY_FILTER_STORAGE_KEY, varietyFilter);
  }, [varietyFilter]);

  useEffect(() => {
    sessionStorage.setItem(PAGE_STORAGE_KEY, String(page));
  }, [page]);

  useEffect(() => {
    sessionStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, String(rowsPerPage));
  }, [rowsPerPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(0);
  }, [isMobile, dateFilter, isLifetime, stateFilter, mandiFilter, varietyFilter]);

  const handleDateChange = (newValue) => {
    if (!newValue) return;
    const value = newValue.format("YYYY-MM-DD");
    if (value > todayValue) return;
    setPendingDate(value);
    setPendingLifetime(false);
  };

  const applyFilter = () => {
    if (!hasPendingChange) return;
    setIsLifetime(pendingLifetime);
    setDateFilter(pendingDate);
    setStateFilter(pendingState);
    setMandiFilter(pendingMandi);
    setVarietyFilter(pendingVariety);
    setPage(0);
  };

  const handleToday = () => {
    setPendingDate(todayValue);
    setPendingLifetime(false);
  };

  const handleClearFilters = () => {
    setPendingDate(todayValue);
    setPendingLifetime(DEFAULT_IS_LIFETIME);
    setPendingState("");
    setPendingMandi("");
    setPendingVariety("");

    setIsLifetime(DEFAULT_IS_LIFETIME);
    setDateFilter(todayValue);
    setStateFilter("");
    setMandiFilter("");
    setVarietyFilter("");
    setPage(0);
  };

  const handleLifetimeToggle = (_, newValue) => {
    if (newValue === null) return;
    setPendingLifetime(newValue === "lifetime");
    if (newValue !== "lifetime" && !pendingDate) setPendingDate(todayValue);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.variety || "").toLowerCase().includes(term) ||
        (r.mandi || "").toLowerCase().includes(term) ||
        (r.state || "").toLowerCase().includes(term)
    );
  }, [rows, search]);

  const handleCopyTable = async () => {
    if (!filteredRows.length) return;
    const header = [
      "State",
      "Mandi",
      "Variety",
      "Call Date",
      "Hand Qty",
      "Hand Price",
      "Combine Qty",
      "Combine Price",
      "Buyers",
    ].join("\t");

    const body = filteredRows
      .map((r) =>
        [
          r.state,
          r.mandi,
          r.variety,
          formatDate(r.callDate),
          formatNumber(r.handQty),
          formatPriceRange(r.handPriceMin, r.handPriceMax) || "-",
          formatNumber(r.combineQty),
          r.combinePrice !== null ? `₹${r.combinePrice}` : "-",
          formatBuyers(r.buyers) || "-",
        ].join("\t")
      )
      .join("\n");

    try {
      await navigator.clipboard.writeText(`${header}\n${body}`);
    } catch (err) {
      console.error("Copy table error:", err);
    }
  };

  const reportCards = [
    {
      key: "totalIVRPartner",
      label: "Total IVR Partners",
      value: reportStats?.totalIVRPartner,
      icon: <DescriptionRoundedIcon />,
      gradient: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
    },
    {
      key: "formSubmit",
      label: "Submitted Reports",
      value: reportStats?.formSubmit,
      icon: <CheckRoundedIcon />,
      gradient: "linear-gradient(135deg,#16A34A 0%,#22D3EE 100%)",
    },
    {
      key: "pending",
      label: "Pending Reports",
      value: reportStats?.pending,
      icon: <EventBusyRoundedIcon />,
      gradient: "linear-gradient(135deg,#F59E0B 0%,#F97316 100%)",
      path: "/pending-mandi-report",
    },
  ];

  const mobileHeadCellSx = {
    px: 0.85,
    py: 0.85,
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.55)",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
    whiteSpace: "nowrap",
  };
  const mobileBodyCellSx = {
    px: 0.85,
    py: 0.9,
    fontSize: "0.74rem",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
    verticalAlign: "top",
  };
  const stickyColSx = {
    position: "sticky",
    left: 0,
    zIndex: 1,
    boxShadow: "2px 0 4px -2px rgba(15,23,42,0.18)",
  };

  const QtyPriceCell = ({ qty, price, icon: Icon, iconColor }) => (
    <Stack spacing={0.3}>
      <Stack direction="row" spacing={0.4} alignItems="center">
        {Icon && <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0 }} />}
        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{formatNumber(qty)}</Typography>
      </Stack>
      <Typography noWrap sx={{ fontSize: "0.64rem", color: "rgba(15,23,42,0.5)" }}>
        {price}
      </Typography>
    </Stack>
  );

  const pageCount = pagination?.totalPages || 1;
  const totalEntries = pagination?.total ?? 0;
  const visiblePageNumbers = useMemo(() => getPageNumbers(page, pageCount), [page, pageCount]);

  const filterLabelSuffix = [
    isLifetime ? "all time" : dateFilter ? formatDate(dayjs(dateFilter).toISOString()) : null,
    stateFilter || null,
    mandiFilter || null,
    varietyFilter || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 1.5, sm: 2.5, md: 4 },
          overflowX: "hidden",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          rowGap={1.5}
          sx={{ mb: { xs: 2, md: 3 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ border: "1px solid #E0E0E0", borderRadius: "12px" }}>
                <MenuRoundedIcon />
              </IconButton>
            )}
            <Box
              sx={{
                width: 52,
                height: 52,
                flexShrink: 0,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
              }}
            >
              <DescriptionRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800}>
                IVR Manual Reports
              </Typography>
              <Typography sx={{ color: "rgba(15,23,42,0.55)", fontSize: "0.85rem" }}>
                {totalEntries ? `${totalEntries} report${totalEntries === 1 ? "" : "s"}` : "View variety-wise manual call reports"}
                {filterLabelSuffix ? ` · ${filterLabelSuffix}` : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            onClick={() => setAddOpen(true)}
            startIcon={<AddRoundedIcon />}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 1.75,
              boxShadow: "none",
              whiteSpace: "nowrap",
              background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
              "&:hover": { background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)", boxShadow: "none" },
            }}
          >
            {isXs ? "Add" : "Add report"}
          </Button>
          <ToggleButtonGroup
            value="table"
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{
              background: "#fff",
              borderRadius: "10px",
              p: 0.4,
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 4px 12px rgba(15,23,42,0.04)",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "8px !important",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                color: "rgba(15,23,42,0.55)",
                px: 1.5,
                gap: 0.6,
              },
              "& .Mui-selected": {
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%) !important",
                color: "#fff !important",
              },
            }}
          >
            <ToggleButton value="table">
              <ListAltRoundedIcon sx={{ fontSize: 17 }} />
              {!isXs && "Table"}
            </ToggleButton>
            <ToggleButton value="analytics">
              <InsightsRoundedIcon sx={{ fontSize: 17 }} />
              {!isXs && "Analytics"}
            </ToggleButton>
          </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Card
          elevation={0}
          sx={{
            borderRadius: "14px",
            p: { xs: 1.25, sm: 1.75 },
            mb: { xs: 1.5, md: 2 },
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ mb: 1.25, pb: 1.25, borderBottom: "1px solid rgba(15,23,42,0.07)" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                }}
              >
                <CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#0F172A" }}>
                Filter reports
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.75}>
              <Chip
                size="small"
                label={hasPendingChange ? "Unapplied changes" : "Up to date"}
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                  color: hasPendingChange ? "#B45309" : "#166534",
                  background: hasPendingChange ? "rgba(245,158,11,0.12)" : "rgba(22,163,74,0.10)",
                }}
              />

              <ToggleButtonGroup
                value={pendingLifetime ? "lifetime" : "date"}
                exclusive
                onChange={handleLifetimeToggle}
                size="small"
                sx={{
                  background: "#F8FAFC",
                  borderRadius: "9px",
                  p: 0.3,
                  border: "1px solid rgba(15,23,42,0.08)",
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: "7px !important",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    color: "rgba(15,23,42,0.55)",
                    px: 1.1,
                    py: 0.35,
                    gap: 0.5,
                  },
                  "& .Mui-selected": {
                    background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%) !important",
                    color: "#fff !important",
                  },
                }}
              >
                <ToggleButton value="date">
                  <TodayRoundedIcon sx={{ fontSize: 15 }} />
                  {!isXs && "By date"}
                </ToggleButton>
                <ToggleButton value="lifetime">
                  <AllInclusiveRoundedIcon sx={{ fontSize: 15 }} />
                  {!isXs && "Lifetime"}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          <Grid container spacing={1.25} alignItems="flex-end">
            <Grid item xs={6} sm={6} md={3}>
              <Typography sx={{ fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "rgba(15,23,42,0.45)", mb: 0.4 }}>
                Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={pendingDate ? dayjs(pendingDate) : null}
                  onChange={handleDateChange}
                  maxDate={dayjs(todayValue)}
                  disabled={pendingLifetime}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <EventRoundedIcon sx={{ fontSize: 17, color: "rgba(15,23,42,0.45)" }} />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "9px",
                      background: pendingLifetime ? "rgba(15,23,42,0.03)" : "rgba(37,99,235,0.04)",
                      "& fieldset": { borderColor: "rgba(15,23,42,0.12)" },
                      "&:hover fieldset": { borderColor: "rgba(37,99,235,0.4)" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                    "& input": { cursor: "pointer", py: 0.85 },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Typography sx={{ fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "rgba(15,23,42,0.45)", mb: 0.4 }}>
                State
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={pendingState}
                onChange={(e) => setPendingState(e.target.value)}
                sx={{ borderRadius: "9px" }}
              >
                <MenuItem value="">All states</MenuItem>
                {filterOptions.states.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Typography sx={{ fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "rgba(15,23,42,0.45)", mb: 0.4 }}>
                Mandi
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={pendingMandi}
                onChange={(e) => setPendingMandi(e.target.value)}
                sx={{ borderRadius: "9px" }}
              >
                <MenuItem value="">All mandis</MenuItem>
                {filterOptions.mandis.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Typography sx={{ fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "rgba(15,23,42,0.45)", mb: 0.4 }}>
                Variety
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={pendingVariety}
                onChange={(e) => setPendingVariety(e.target.value)}
                sx={{ borderRadius: "9px" }}
              >
                <MenuItem value="">All varieties</MenuItem>
                {filterOptions.varieties.map((v) => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.25 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={!hasAppliedFilter && !hasPendingChange}
              onClick={handleClearFilters}
              startIcon={<ClearRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: "9px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                py: 0.5,
                borderColor: "rgba(220,38,38,0.3)",
                color: "#B91C1C",
                whiteSpace: "nowrap",
                "&:hover": { borderColor: "#DC2626", background: "rgba(220,38,38,0.04)" },
              }}
            >
              Clear 
            </Button>

            <Button
              size="small"
              variant="outlined"
              disabled={pendingDate === todayValue && !pendingLifetime}
              onClick={handleToday}
              sx={{
                borderRadius: "9px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                py: 0.5,
                borderColor: "rgba(15,23,42,0.12)",
                color: "#0F172A",
                whiteSpace: "nowrap",
              }}
            >
              Today
            </Button>

            <Button
              size="small"
              variant="contained"
              disabled={!hasPendingChange || loading}
              onClick={applyFilter}
              startIcon={<CheckRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: "9px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                py: 0.5,
                boxShadow: "none",
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                whiteSpace: "nowrap",
                "&:hover": { background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)" },
              }}
            >
              Apply filters
            </Button>
          </Stack>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, md: 3 }, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 1.5, sm: 2, md: 2 } }}>
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Skeleton variant="rounded" height={isXs ? 110 : 130} sx={{ borderRadius: "18px" }} />
              </Grid>
            ))}

          {!loading &&
            reportCards.map((card) => {
              const isClickable = Boolean(card.path);
              return (
                <Grid item xs={12} sm={4} key={card.key}>
                  <Card
                    elevation={0}
                    onClick={isClickable ? () => navigate(card.path) : undefined}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(card.path);
                            }
                          }
                        : undefined
                    }
                    sx={{
                      borderRadius: { xs: "14px", md: "18px" },
                      p: { xs: 1.5, sm: 2, md: 2.5 },
                      height: "100%",
                      background: "#fff",
                      border: "1px solid rgba(15,23,42,0.06)",
                      boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
                      cursor: isClickable ? "pointer" : "default",
                      transition: "transform 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": isClickable
                        ? {
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 28px rgba(15,23,42,0.09)",
                          }
                        : undefined,
                      "&:focus-visible": isClickable
                        ? {
                            outline: "2px solid #2563EB",
                            outlineOffset: "2px",
                          }
                        : undefined,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            width: { xs: 38, md: 44 },
                            height: { xs: 38, md: 44 },
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            background: card.gradient,
                            flexShrink: 0,
                            "& svg": { fontSize: { xs: 20, md: 24 } },
                          }}
                        >
                          {card.icon}
                        </Box>

                        <Box>
                          <Typography sx={{ color: "rgba(15,23,42,0.55)", fontWeight: 600, fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            {card.label}
                          </Typography>
                          <Typography fontWeight={800} sx={{ fontSize: { xs: "1.25rem", md: "1.6rem" } }} color="#0F172A">
                            {typeof card.value === "number" ? card.value.toLocaleString() : "—"}
                          </Typography>
                        </Box>
                      </Stack>

                      {isClickable && (
                        <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: "rgba(15,23,42,0.3)", flexShrink: 0 }} />
                      )}
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
        </Grid>

        <Card elevation={0} sx={{ borderRadius: "16px", p: { xs: 1.75, sm: 2.25 }, border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 6px 18px rgba(15,23,42,0.04)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} mb={2}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <ListAltRoundedIcon sx={{ color: "#0F172A" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>Report Table</Typography>
            </Stack>

            <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Tooltip title="Searches within the currently loaded page">
                <TextField
                  size="small"
                  placeholder="Search state, mandi or variety..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={!rows.length}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { xs: 0, sm: 220 }, flexGrow: { xs: 1, sm: 0 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Tooltip>

              <Select
                size="small"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{ borderRadius: "10px", minWidth: 90 }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>{n} / page</MenuItem>
                ))}
              </Select>

              <Tooltip title="Copy table">
                <IconButton onClick={handleCopyTable} disabled={!rows.length} sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "10px" }}>
                  <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {loading ? (
            !isMobile ? (
              <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: "rgba(15,23,42,0.02)" }}>
                      {["#", "Mandi", "State", "Variety", "Total Arrival", "Hand Qty", "Hand Price", "Combine Qty", "Combine Price", "Buyers", "Action"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: rowsPerPage > 10 ? 10 : rowsPerPage }, (_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: DESKTOP_COLUMN_COUNT }, (_, c) => (
                          <TableCell key={c}><Skeleton variant="text" width={c === 0 ? 20 : "80%"} height={24} /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...mobileHeadCellSx, ...stickyColSx, minWidth: 88 }}>Mandi</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 78 }}>State</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 72 }}>Variety</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 88 }}>Hand</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 88 }}>Combine</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 120, borderRight: "none" }}>Buyers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: 6 }, (_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: MOBILE_COLUMN_COUNT }, (_, c) => (
                          <TableCell key={c} sx={mobileBodyCellSx}>
                            <Skeleton variant="text" width="70%" height={16} />
                            {(c === 2 || c === 3) && <Skeleton variant="text" width="50%" height={13} />}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )
          ) : rows.length === 0 ? (
            <Box sx={{ minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", px: 2 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(37,99,235,0.08)", color: "#2563EB", mb: 1.5 }}>
                <EventBusyRoundedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>No reports found</Typography>
              <Typography sx={{ color: "rgba(15,23,42,0.5)", fontSize: "0.85rem", mt: 0.5 }}>
                No manual IVR reports for this selection.
              </Typography>
            </Box>
          ) : !isMobile ? (
            <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
              <Table sx={{
                "& th, & td": { borderBottom: "1px solid rgba(15,23,42,0.10)", borderRight: "1px solid rgba(15,23,42,0.10)", fontSize: "0.95rem" },
                "& th:last-of-type, & td:last-of-type": { borderRight: "none" },
                "& tr:last-of-type td": { borderBottom: "none" },
              }}>
                <TableHead>
                  <TableRow sx={{ background: "rgba(15,23,42,0.02)" }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mandi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Variety</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Total Arrival</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hand Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hand Price</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Combine Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Combine Price</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Buyers</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length > 0 ? filteredRows.map((row, idx) => (
                    <TableRow key={row.key} hover>
                      <TableCell sx={{ color: "rgba(15,23,42,0.55)" }}>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>
                        <Chip icon={<StorefrontRoundedIcon sx={{ fontSize: 17 }} />} label={row.mandi || "-"} size="small" sx={{ background: "rgba(22,163,74,0.10)", color: "#166534", fontWeight: 600, fontSize: "0.85rem" }} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: "0.9rem", color: "rgba(15,23,42,0.7)" }}>{row.state || "-"}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{row.variety || "-"}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Inventory2RoundedIcon sx={{ fontSize: 17, color: "#16A34A" }} />
                          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                            {formatNumber(row.totalArrival)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Inventory2RoundedIcon sx={{ fontSize: 17, color: "#7C3AED" }} />
                          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{formatNumber(row.handQty)}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {formatPriceRange(row.handPriceMin, row.handPriceMax) ? (
                          <Chip label={formatPriceRange(row.handPriceMin, row.handPriceMax)} size="small" sx={{ background: "rgba(124,58,237,0.10)", color: "#5B21B6", fontWeight: 600, fontSize: "0.85rem" }} />
                        ) : (
                          <Typography sx={{ color: "rgba(15,23,42,0.35)" }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <WidgetsRoundedIcon sx={{ fontSize: 17, color: "#D97706" }} />
                          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{formatNumber(row.combineQty)}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.combinePrice !== null ? `₹${row.combinePrice}` : "₹ —"} size="small" sx={{ background: "rgba(217,119,6,0.12)", color: "#92400E", fontWeight: 600, fontSize: "0.85rem" }} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200, maxWidth: 260 }}>
                        {row.buyers && row.buyers.length > 0 ? (
                          <Tooltip title={formatBuyers(row.buyers)} arrow>
                            <Typography noWrap sx={{ fontSize: "0.95rem", color: "#0F172A" }}>
                              {formatBuyers(row.buyers)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ color: "rgba(15,23,42,0.35)" }}>—</Typography>
                        )}
                        {row.remarks && (
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, mt: 0.25 }} color="#B91C1C">
                            {row.remarks}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit report">
                          <IconButton
                            size="small"
                            onClick={() => setEditRow(row)}
                            sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px" }}
                          >
                            <EditRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={DESKTOP_COLUMN_COUNT} sx={{ textAlign: "center", py: 5 }}>
                        <Typography sx={{ color: "rgba(15,23,42,0.5)" }}>No matching rows on this page</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <>
              {filteredRows.length > 0 && (
                <Typography sx={{ fontSize: "0.72rem", color: "rgba(15,23,42,0.45)", mb: 0.75, ml: 0.25 }}>
                  Swipe left for more →
                </Typography>
              )}
              <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)", WebkitOverflowScrolling: "touch" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...mobileHeadCellSx, ...stickyColSx, minWidth: 88 }}>Mandi</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 78 }}>State</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 72 }}>Variety</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 88 }}>Hand</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 88 }}>Combine</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 120, borderRight: "none" }}>Buyers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length > 0 ? filteredRows.map((row, idx) => {
                      const zebraSx = idx % 2 === 1 ? { background: "rgba(15,23,42,0.015)" } : {};
                      const stickyBg = idx % 2 === 1 ? "#FBFCFD" : "#fff";
                      const handPrice = formatPriceRange(row.handPriceMin, row.handPriceMax) || "-";
                      const combinePrice = row.combinePrice !== null ? `₹${row.combinePrice}` : "-";

                      return (
                        <TableRow key={row.key} sx={zebraSx}>
                          <TableCell sx={{ ...mobileBodyCellSx, ...stickyColSx, background: stickyBg, fontWeight: 700 }}>
                            <Stack direction="row" spacing={0.5} alignItems="flex-start" justifyContent="space-between">
                              <Stack spacing={0.1}>
                                <Typography sx={{ fontSize: "0.76rem", fontWeight: 700 }}>{row.mandi || "-"}</Typography>
                                <Typography sx={{ fontSize: "0.6rem", color: "rgba(15,23,42,0.5)" }}>{formatDate(row.callDate)}</Typography>
                              </Stack>
                              <IconButton
                                size="small"
                                aria-label="Edit report"
                                onClick={() => setEditRow(row)}
                                sx={{ p: 0.3, flexShrink: 0 }}
                              >
                                <EditRoundedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell sx={mobileBodyCellSx}>{row.state || "-"}</TableCell>
                          <TableCell sx={mobileBodyCellSx}>{row.variety || "-"}</TableCell>
                          <TableCell sx={mobileBodyCellSx}>
                            <QtyPriceCell qty={row.handQty} price={handPrice} icon={Inventory2RoundedIcon} iconColor="#7C3AED" />
                          </TableCell>
                          <TableCell sx={mobileBodyCellSx}>
                            <QtyPriceCell qty={row.combineQty} price={combinePrice} icon={WidgetsRoundedIcon} iconColor="#D97706" />
                          </TableCell>
                          <TableCell sx={{ ...mobileBodyCellSx, borderRight: "none" }}>
                            <Stack spacing={0.4}>
                              {row.buyers && row.buyers.length > 0 ? (
                                <Tooltip title={formatBuyers(row.buyers)} arrow>
                                  <Typography
                                    sx={{
                                      fontSize: "0.74rem",
                                      maxWidth: 130,
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {formatBuyers(row.buyers)}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography sx={{ fontSize: "0.74rem", color: "rgba(15,23,42,0.35)" }}>—</Typography>
                              )}
                              {row.remarks && (
                                <Typography sx={{ fontSize: "0.62rem", fontWeight: 600 }} color="#B91C1C">
                                  {row.remarks}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={MOBILE_COLUMN_COUNT} sx={{ textAlign: "center", py: 5, borderRight: "none" }}>
                          <Typography sx={{ color: "rgba(15,23,42,0.5)" }}>No matching rows on this page</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}

          {rows.length > 0 && pagination && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
              mt={2}
            >
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.55)" }}>
                Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalEntries)} of {totalEntries} entries
              </Typography>

              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ maxWidth: "100%" }}
              >
                <IconButton
                  size="small"
                  aria-label="Previous page"
                  disabled={!pagination.hasPreviousPage || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px", flexShrink: 0 }}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    overflowX: "auto",
                    py: 0.25,
                    "&::-webkit-scrollbar": { display: "none" },
                    scrollbarWidth: "none",
                  }}
                >
                  {visiblePageNumbers.map((p, idx) =>
                    p === "..." ? (
                      <Typography
                        key={`dots-${idx}`}
                        sx={{
                          width: 24,
                          textAlign: "center",
                          fontSize: "0.85rem",
                          color: "rgba(15,23,42,0.4)",
                          flexShrink: 0,
                        }}
                      >
                        …
                      </Typography>
                    ) : (
                      <Box
                        key={p}
                        onClick={() => !loading && setPage(p)}
                        sx={{
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          cursor: loading ? "default" : "pointer",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          flexShrink: 0,
                          border: p === page ? "1px solid #2563EB" : "1px solid transparent",
                          color: p === page ? "#2563EB" : "#0F172A",
                          background: p === page ? "rgba(37,99,235,0.08)" : "transparent",
                        }}
                      >
                        {p + 1}
                      </Box>
                    )
                  )}
                </Stack>

                <IconButton
                  size="small"
                  aria-label="Next page"
                  disabled={!pagination.hasNextPage || loading}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px", flexShrink: 0 }}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Card>
      </Box>

      <AddManualReportDialog
        open={addOpen}
        token={token}
        defaultDate={isLifetime ? undefined : dateFilter}
        onClose={() => setAddOpen(false)}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />

      <EditManualReportDialog
        open={Boolean(editRow)}
        row={editRow}
        reportId={editRow?.id}
        token={token}
        onClose={() => setEditRow(null)}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
    </Box>
  );
};

export default IvrCallLogs;